﻿import {nanoid} from "@2d-front-monorepo/core";
import {
	Context,
	createContext,
	JSX,
	ReactNode,
	RefObject,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";

import {
	IMvvmContextState,
	MvvmContextInitialState,
	MvvmContextState,
	MvvmContextValidationMethod
} from "./MvvmContextState";

export const MvvmContext: Context<MvvmDataProviderRef<any> | undefined> = createContext<MvvmDataProviderRef<any> | undefined>(undefined);

export type MvvmDataProviderRef<T extends object> = {
	context: IMvvmContextState<T>;
}

export type MvvmDataProviderProps<T extends object> = {
	context: IMvvmContextState<T>;
	onSubmit: (data: T) => Promise<void>;
	children: ReactNode;
}

export function MvvmDataProvider<T extends object>(props: MvvmDataProviderProps<T>): JSX.Element {
	const {context, onSubmit, children} = props;
	const id = useMemo(() => `form-${nanoid()}`, []);

	const upperContext = useContext(MvvmContext)?.context as MvvmContextState<T>;

	if (upperContext == null){
		return (
			<MvvmContext.Provider value={{context}}>
				<form noValidate={true}
				      id={id}
				      method="post"
				      inert={context.isSubmitting()}
				      onSubmit={(e) => (context as MvvmContextState<T>).handleSubmit(e, onSubmit)}>
					{children}
				</form>
			</MvvmContext.Provider>
		);
	}

	(upperContext as MvvmContextState<T>).registerChildren(context as MvvmContextState<any>);
	return (<MvvmContext.Provider value={{context}}>{children}</MvvmContext.Provider>);
}

export type MvvmContextProps<T extends object> = {
	initialState: MvvmContextInitialState<T>;
	contextRef?: RefObject<MvvmDataProviderRef<T>>;
	onChange?: ((data: T) => Promise<void>) | ((data: T) => void);
	runValidationOnChange?: boolean;
	validation?: MvvmContextValidationMethod<T>;
	validateOnMount?: boolean;
}

export function initializeMvvmContext<T extends object>(props: MvvmContextProps<T>, deps?: readonly unknown[]): IMvvmContextState<T> {
	const {initialState, contextRef, onChange, runValidationOnChange, validation, validateOnMount} = props;

	const [_, dispatch] = useState(-1);
	const mountedRef = useRef(true);
	const prevDepsRef = useRef<readonly unknown[] | undefined>(undefined);

	const stateRef = useRef<MvvmContextState<T>>(undefined);

	const depsChanged = deps !== undefined &&
		(prevDepsRef.current === undefined ||
			deps.length !== prevDepsRef.current.length ||
			deps.some((dep, index) => dep !== prevDepsRef.current![index]));

	if (stateRef.current == null || depsChanged) {
		if (stateRef.current != null) {
			stateRef.current.dispose();
		}

		stateRef.current = new MvvmContextState<T>(initialState, dispatch);
		prevDepsRef.current = deps;
	}

	const context = stateRef.current;

	useImperativeHandle(contextRef, () => ({
		context: context
	}), [context]);

	context.setOnChange(onChange, runValidationOnChange ?? false);
	context.setValidation(validation);

	useEffect(() => {
		if (validateOnMount) context.triggerValidation();
	}, [validateOnMount, context]);

	useEffect(() => {
		//dance around the double dispatch React does in dev mode.
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			setTimeout(() => {
				if (!mountedRef.current) {
					context.dispose();
				}
			}, 1);
		}
	}, []);

	return context;
}

export function useMvvmContext<T extends object>(): IMvvmContextState<T> {
	const context = useContext(MvvmContext);
	if (context === undefined) {
		throw new Error("useMvvmContext must be used within a MvvmDataProvider");
	}

	return context.context;
}
